import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError, downloadJson } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Printer, Pencil, Trash2, Plus, ArrowRightLeft, MessageCircle, Download } from "lucide-react";
import { toast } from "sonner";

const inr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n || 0);

const AGING_BADGE = {
  green: "aging-badge-green",
  yellow: "aging-badge-yellow",
  red: "aging-badge-red",
  critical: "aging-badge-critical",
  paid: "aging-badge-paid",
};

export function ExportButton() {
  const { user } = useAuth();
  if (!["admin", "owner"].includes(user?.role)) return null;
  const run = () =>
    downloadJson("/bills-export.csv", `bill_list_${new Date().toISOString().slice(0, 10)}.csv`)
      .then(() => toast.success("Bill list downloaded"))
      .catch((e) => toast.error(formatApiError(e)));
  return (
    <Button onClick={run} variant="outline" className="py-5 text-base rounded-full" data-testid="bills-export-btn">
      <Download size={16} strokeWidth={2} /> Download bill list
    </Button>
  );
}


export default function Bills() {
  const [bills, setBills] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [buyersList, setBuyersList] = useState([]);
  const [q, setQ] = useState("");
  const [params, setParams] = useSearchParams();
  const [empFilter, setEmpFilter] = useState(params.get("employee") || "all");
  const [filter, setFilter] = useState(params.get("filter") || "all");
  const [dateRange, setDateRange] = useState(params.get("range") || "all");
  const [fromDate, setFromDate] = useState(params.get("from") || "");
  const [toDate, setToDate] = useState(params.get("to") || "");
  const [docType, setDocType] = useState(params.get("doctype") || "all");
  const buyerParam = params.get("buyer") || "";
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setParams((p) => {
      const np = new URLSearchParams(p);
      if (filter === "all") np.delete("filter"); else np.set("filter", filter);
      if (empFilter === "all") np.delete("employee"); else np.set("employee", empFilter);
      return np;
    }, { replace: true });
  }, [filter, empFilter, setParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: b }, { data: e }, { data: bu }] = await Promise.all([
        api.get("/bills"),
        api.get("/employees"),
        api.get("/buyers"),
      ]);
      setBills(b);
      setEmployees(e);
      setBuyersList(bu);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Move to Recycle Bin?")) return;
    try {
      await api.delete(`/bills/${id}`);
      toast.success("Moved to recycle bin");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const convertToBill = async (b) => {
    if (!window.confirm(`Convert challan ${b.bill_number} to a bill?`)) return;
    try {
      const { data } = await api.post(`/bills/${b.id}/convert-to-bill`);
      toast.success(`Converted to ${data.bill_number}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const shareOnWhatsApp = (b) => {
    const balance = Math.max(Math.round(b.balance || 0), 0);
    const total = Math.round(b.amount || 0);
    const paid = Math.round(b.paid_amount || 0);
    const label = b.doc_type === "challan" ? "Challan" : "Bill";
    const buyerName = b.buyer?.name || "there";
    const msg =
      `Hi ${buyerName}, gentle reminder — ${label} ${b.bill_number} dated ${b.bill_date} ` +
      `has a pending balance of ₹${balance.toLocaleString("en-IN")}. ` +
      `Total ₹${total.toLocaleString("en-IN")}, Received ₹${paid.toLocaleString("en-IN")}. ` +
      `Kindly settle at the earliest. Thanks!`;
    const raw = (b.buyer?.contact || "").replace(/\D/g, "");
    const phone = raw ? (raw.length === 10 ? `91${raw}` : raw) : "";
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener");
  };

  const filtered = bills
    .filter((b) => (buyerParam === "" ? true : b.buyer_id === buyerParam))
    .filter((b) => (empFilter === "all" ? true : b.employee_id === empFilter))
    .filter((b) => docType === "all" ? true : (b.doc_type || "bill") === docType)
    .filter((b) => {
      if (dateRange === "all") return true;
      const bd = b.bill_date ? new Date(b.bill_date) : null;
      if (!bd || isNaN(bd)) return true;
      if (dateRange === "custom") {
        if (fromDate && bd < new Date(fromDate)) return false;
        if (toDate && bd > new Date(toDate)) return false;
        return true;
      }
      const days = Math.floor((Date.now() - bd.getTime()) / 86400000);
      return days <= parseInt(dateRange, 10);
    })
    .filter((b) => {
      if (filter === "partial") return b.status === "partial";
      if (filter === "unpaid") return b.status !== "paid";
      if (filter === "paid") return b.status === "paid";
      if (filter === "red") return ["red", "critical"].includes(b.aging);
      return true;
    })
      .filter((b) => {
      const s = q.trim().toLowerCase();
      if (!s) return true;
      return (
        (b.bill_number || "").toLowerCase().includes(s) ||
        (b.buyer?.name || "").toLowerCase().includes(s) ||
        (b.employee?.name || "").toLowerCase().includes(s)
      );
    });

  const buyerName = buyerParam
    ? bills.find((b) => b.buyer_id === buyerParam)?.buyer?.name ||
      buyersList.find((x) => x.id === buyerParam)?.name ||
      ""
    : "";
  const buyerPending = filtered.reduce((s, b) => s + Math.max(b.balance || 0, 0), 0);

  const clearBuyer = () => {
    setParams((p) => {
      const np = new URLSearchParams(p);
      np.delete("buyer");
      return np;
    }, { replace: true });
  };

  return (
    <div className="p-8 lg:p-14 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between mb-10 animate-fade-up">
        <div>
          <div className="text-[11px] uppercase tracking-[0.35em] text-neutral-500">
            Records
          </div>
          <h1 className="font-display text-6xl font-bold tracking-tight mt-2">
            {buyerParam ? (buyerName || "Buyer") : "Bills"}
          </h1>
          {buyerParam && (
            <div className="mt-3 flex items-center gap-3">
              <span className="chip bg-forest-100 text-forest-700" data-testid="buyer-filter-chip">
                Transactions only · {filtered.length} bills ·{" "}
                <span className="font-mono-num">{inr(buyerPending)}</span> pending
              </span>
              <button
                onClick={clearBuyer}
                data-testid="buyer-filter-clear"
                className="text-xs text-neutral-500 hover:text-neutral-900 underline underline-offset-2"
              >
                Show all bills
              </button>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <ExportButton />
          <Link to="/bills/new" data-testid="bills-new-btn">
            <Button className="btn-primary py-5 text-base">
              <Plus size={16} strokeWidth={2} /> New Bill
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-6 animate-fade-up">
        <Input
          placeholder="Search by bill number, buyer, employee…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="hygenix-input max-w-xs"
          data-testid="bills-search"
        />
        <Select value={empFilter} onValueChange={setEmpFilter}>
          <SelectTrigger className="hygenix-input w-52" data-testid="bills-employee-filter">
            <SelectValue placeholder="All employees" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All employees</SelectItem>
            {employees.map((e) => (
              <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex rounded-full border border-neutral-200 bg-white overflow-hidden">
          {[
            ["all", "All docs"],
            ["bill", "Bills"],
            ["challan", "Challans"],
          ].map(([f, label]) => (
            <button
              key={f}
              onClick={() => setDocType(f)}
              data-testid={`bills-doctype-${f}`}
              className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                docType === f
                  ? f === "challan"
                    ? "bg-[#4238C1] text-white"
                    : f === "bill"
                    ? "bg-[#0F2F1E] text-white"
                    : "bg-neutral-800 text-white"
                  : "text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex rounded-full border border-neutral-200 bg-white overflow-hidden">
          {[
            ["all", "All"],
            ["unpaid", "Unpaid"],
            ["partial", "Partial payments"],
            ["red", "Overdue"],
            ["paid", "Fully paid"],
          ].map(([f, label]) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`bills-filter-${f}`}
              className={`px-4 py-2 text-xs uppercase tracking-wider transition-colors ${
                filter === f ? "bg-[#0F2F1E] text-white" : "text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="flex rounded-full border border-neutral-200 bg-white overflow-hidden">
          {[
            ["all", "All time"],
            ["15", "15d"],
            ["30", "30d"],
            ["45", "45d"],
            ["60", "60d"],
            ["custom", "Custom"],
          ].map(([f, label]) => (
            <button
              key={f}
              onClick={() => setDateRange(f)}
              data-testid={`bills-daterange-${f}`}
              className={`px-3 py-2 text-xs uppercase tracking-wider transition-colors ${
                dateRange === f ? "bg-[#EA5B3A] text-white" : "text-neutral-700 hover:bg-neutral-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {dateRange === "custom" && (
          <div className="flex items-center gap-2">
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              className="hygenix-input w-40" data-testid="bills-custom-from" />
            <span className="text-xs text-neutral-500">to</span>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              className="hygenix-input w-40" data-testid="bills-custom-to" />
          </div>
        )}
      </div>

      <div className="space-y-3 stagger" data-testid="bills-list">
        {loading && <div className="text-sm text-neutral-500">Loading…</div>}
        {!loading && filtered.length === 0 && (
          <div className="card-surface p-12 text-center text-neutral-500">
            No bills. Click <span className="font-semibold text-neutral-900">New Bill</span> to record one.
          </div>
        )}
        {filtered.map((b) => (
          <div
            key={b.id}
            className="card-surface flex items-stretch gap-4 pl-4 pr-4 py-4 overflow-hidden"
            data-testid={`bill-row-${b.bill_number}`}
          >
            {/* Big aging badge (age of the bill itself, right in front) */}
            <div className={`aging-badge ${AGING_BADGE[b.aging] || "aging-badge-green"} my-1`}
                 data-testid={`bill-aging-badge-${b.bill_number}`}>
              <div className="aging-badge-num">{b.days_since ?? 0}</div>
              <div className="aging-badge-unit">day</div>
            </div>

            {/* Follow-up ticker — only when a follow-up date is set */}
            {b.days_since_followup !== null && b.days_since_followup !== undefined && (
              <div
                className={`aging-badge ${
                  b.partial_overdue ? "aging-badge-red" : "aging-badge-yellow"
                }`}
                data-testid={`bill-followup-badge-${b.bill_number}`}
                title="Days since given to employee"
              >
                <div className="aging-badge-num">{b.days_since_followup}</div>
                <div className="aging-badge-unit">day/EMP</div>
              </div>
            )}

            <div className="flex-1 min-w-0 grid grid-cols-12 items-center gap-3">
              <div className="col-span-12 md:col-span-5 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-mono-num text-sm">{b.bill_number}</div>
                  <span className={b.doc_type === "challan" ? "badge-doctype-challan" : "badge-doctype-bill"}>
                    {b.doc_type === "challan" ? "Challan" : "Bill"}
                  </span>
                </div>
                <div className="text-sm font-semibold truncate mt-0.5">
                  {b.buyer?.name || "—"}
                </div>
                <div className="text-xs text-neutral-500">
                  via {b.employee?.name || "—"} · {b.bill_date}
                </div>
              </div>

              <div className="col-span-12 md:col-span-4">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg py-1.5 px-2 bg-neutral-50">
                    <div className="text-[9px] uppercase tracking-wider text-neutral-500 font-semibold">Total</div>
                    <div className="font-mono-num text-sm font-semibold" data-testid={`bill-total-${b.bill_number}`}>{inr(b.amount)}</div>
                  </div>
                  <div className="rounded-lg py-1.5 px-2 bg-[#E8F5EE]">
                    <div className="text-[9px] uppercase tracking-wider text-[#16A34A] font-semibold">Received</div>
                    <div className="font-mono-num text-sm font-semibold text-[#0F6E33]" data-testid={`bill-received-${b.bill_number}`}>{inr(b.paid_amount)}</div>
                  </div>
                  <div className={`rounded-lg py-1.5 px-2 ${b.balance > 0 ? "bg-[#FEE7DF]" : "bg-neutral-50"}`}>
                    <div className={`text-[9px] uppercase tracking-wider font-semibold ${b.balance > 0 ? "text-[#C4361B]" : "text-neutral-500"}`}>Pending</div>
                    <div className={`font-mono-num text-sm font-semibold ${b.balance > 0 ? "text-[#C4361B]" : "text-neutral-600"}`} data-testid={`bill-pending-${b.bill_number}`}>{inr(b.balance)}</div>
                  </div>
                </div>
              </div>

              <div className="col-span-8 md:col-span-1 flex flex-wrap gap-1.5 items-center">
                {b.order_status && b.order_status !== "pending" && (
                  <span className="chip bg-neutral-100 text-neutral-700 capitalize">
                    {b.order_status.replace("_", " ")}
                  </span>
                )}
                {b.status === "partial" && (
                  <span className="badge-partial" data-testid={`bill-partial-badge-${b.id}`}>
                    Part paid
                  </span>
                )}
              </div>

              <div className="col-span-4 md:col-span-2 flex justify-end items-center gap-1">
                {b.doc_type === "challan" && (
                  <button onClick={() => convertToBill(b)}
                          className="p-2 rounded-lg hover:bg-[#EDE9FE] text-[#4238C1]"
                          title="Convert to Bill"
                          data-testid={`bill-convert-${b.bill_number}`}>
                    <ArrowRightLeft size={14} strokeWidth={2} />
                  </button>
                )}
                <button onClick={() => shareOnWhatsApp(b)}
                        className="p-2 rounded-lg hover:bg-[#DCFCE7] text-[#16A34A]"
                        title="Share on WhatsApp"
                        data-testid={`bill-whatsapp-${b.bill_number}`}>
                  <MessageCircle size={14} strokeWidth={2} />
                </button>
                <Link to={`/bills/${b.id}/print`} target="_blank"
                      className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-700"
                      data-testid={`bill-print-${b.bill_number}`}>
                  <Printer size={14} strokeWidth={2} />
                </Link>
                <Link to={`/bills/${b.id}/edit`}
                      className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-700"
                      data-testid={`bill-edit-${b.bill_number}`}>
                  <Pencil size={14} strokeWidth={2} />
                </Link>
                <button onClick={() => remove(b.id)}
                        className="p-2 rounded-lg hover:bg-[#FFE5DE] text-[#C4361B]"
                        data-testid={`bill-delete-${b.bill_number}`}>
                  <Trash2 size={14} strokeWidth={2} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
